/**
 * ⚡ Battery Indicator Component
 *
 * Simple battery display - NO CHART
 * Shows icon + percentage for battery-powered devices
 * Replaces the heavy battery chart (saves ~1200 render points)
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

interface BatteryIndicatorProps {
  value: number; // Battery voltage or percentage
  theme: any;
  isArabic: boolean;
  voltage?: boolean; // If true, shows voltage; otherwise percentage
}

/**
 * Get battery icon and color based on level
 */
const getBatteryDisplay = (value: number, voltage: boolean = false) => {
  let percentage: number;

  if (voltage) {
    // Convert voltage to percentage (3.0V = 0%, 4.2V = 100%)
    percentage = Math.min(100, Math.max(0, ((value - 3.0) / 1.2) * 100));
  } else {
    percentage = value;
  }

  let icon: keyof typeof Ionicons.glyphMap;
  let color: string;

  if (percentage >= 80) {
    icon = 'battery-full';
    color = '#10b981'; // green
  } else if (percentage >= 50) {
    icon = 'battery-half';
    color = '#f59e0b'; // yellow
  } else if (percentage >= 20) {
    icon = 'battery-half';
    color = '#f97316'; // orange
  } else {
    icon = 'battery-dead';
    color = '#ef4444'; // red
  }

  return { icon, color, percentage: Math.round(percentage) };
};

const BatteryIndicator: React.FC<BatteryIndicatorProps> = ({
  value,
  theme,
  isArabic,
  voltage = true,
}) => {
  const { icon, color, percentage } = getBatteryDisplay(value, voltage);

  return (
    <View style={styles.container}>
      <View style={styles.iconRow}>
        <Ionicons name={icon} size={24} color={color} />
        <View style={styles.textColumn}>
          <Text style={[styles.percentage, { color: theme.colors.text }]}>{percentage}%</Text>
          {voltage && (
            <Text style={[styles.voltage, { color: theme.colors.textSecondary }]}>
              {value.toFixed(2)}V
            </Text>
          )}
        </View>
      </View>

      {/* Optional: Progress bar */}
      <View style={[styles.progressBar, { backgroundColor: theme.colors.border }]}>
        <View
          style={[
            styles.progressFill,
            {
              backgroundColor: color,
              width: `${percentage}%`,
            },
          ]}
        />
      </View>
    </View>
  );
};

export default BatteryIndicator;

const styles = StyleSheet.create({
  container: {
    minWidth: 100,
    gap: 8,
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  textColumn: {
    gap: 2,
  },
  percentage: {
    fontSize: 18,
    fontWeight: '700',
  },
  voltage: {
    fontSize: 11,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
});
